import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useI18n } from "../i18n";

const API = "/api";

// iter72 corte 4 FIX A/B — UN solo contexto de precio (país + plan) para TODA la app.
// CORTE 4-D1 — regla 4: PROHIBIDOS los fallbacks numéricos. Sin datos → null (skeleton).
// País desconocido → "XX" explícito (el backend responde el fallback internacional USD).
const PricingCtx = createContext(null);

export function PricingProvider({ children }) {
  const { t } = useI18n();
  const [country, setCountryState] = useState(() => {
    try { return localStorage.getItem("avisocar_pricing_country") || ""; } catch (_) { return ""; }
  });
  const [planMonths, setPlanMonthsState] = useState(() => {
    try { return Number(localStorage.getItem("avisocar_plan_months")) || 12; } catch (_) { return 12; }
  });
  const [display, setDisplay] = useState(null);
  // RC-WEB-2 — catálogo canónico de planes (mismo contrato que Android/iOS)
  const [plans, setPlans] = useState(null);
  const [configVersion, setConfigVersion] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const reqSeq = useRef(0);

  useEffect(() => {
    if (country) return;
    axios.get(`${API}/geo/detect`)
      .then((r) => setCountryState(((r.data?.country || "").slice(0, 2).toUpperCase()) || "XX"))
      .catch(() => setCountryState("XX"));
  }, [country]);

  useEffect(() => {
    if (!country) return;
    // Protección contra respuestas obsoletas: solo la última selección gana.
    const seq = ++reqSeq.current;
    const controller = new AbortController();
    const iso = country === "XX" ? "" : country;
    setPricingLoading(true);
    Promise.all([
      axios.get(`${API}/pricing/display?country=${iso}`, { signal: controller.signal }),
      axios.get(`${API}/membership/plans?country_code=${iso}`, { signal: controller.signal }),
    ])
      .then(([d, p]) => {
        if (seq !== reqSeq.current) return;
        setDisplay(d.data);
        setPlans(p.data?.plans || []);
        setConfigVersion(p.data?.config_version ?? null);
      })
      .catch(() => {
        if (seq !== reqSeq.current) return;
        setDisplay(null);
        setPlans(null);
      })
      .finally(() => {
        if (seq === reqSeq.current) setPricingLoading(false);
      });
    return () => controller.abort();
  }, [country]);

  const setCountry = useCallback((c) => {
    setCountryState(c);
    try { localStorage.setItem("avisocar_pricing_country", c); } catch (_) { /* ignore */ }
  }, []);
  const setPlanMonths = useCallback((m) => {
    setPlanMonthsState(m);
    try { localStorage.setItem("avisocar_plan_months", String(m)); } catch (_) { /* ignore */ }
  }, []);

  const isPE = display?.country === "PE";
  const ownPrice = !!display?.own_price;
  // RC-WEB-3 §4 — a country without a CONFIGURED local estimate must never be painted
  // with the international fallback amount as if it were its own price.
  const estimateConfigured = display ? display.estimate_configured !== false : false;
  const displayCurrency = display?.display_currency || display?.currency || "";
  const cur = display ? (estimateConfigured ? (display.symbol || display.currency) : (display.pen_symbol || "S/")) : "";
  const planAmount = useCallback((m) => {
    if (!estimateConfigured) return display?.plans?.[String(m)]?.pen ?? null;
    const p = display?.plans?.[String(m)];
    return p ? p.amount : null;
  }, [display, estimateConfigured]);
  const planPen = useCallback((m) => display?.plans?.[String(m)]?.pen ?? null, [display]);
  const priceLabel = useCallback((m) => {
    const a = planAmount(m);
    return a == null ? null : `${cur} ${Number(a).toLocaleString("es")}`;
  }, [cur, planAmount]);
  const penLabel = useCallback((m) => {
    const p = planPen(m);
    return p == null ? null : `${display?.pen_symbol || ""} ${Number(p).toLocaleString("es")}`;
  }, [display, planPen]);

  // RC-WEB-1 §17 — cobro real (SIEMPRE PEN) vs estimación mostrada (aproximada).
  // `display_is_charge_currency === false` ⇒ lo que se ve es una conversión, no el cobro.
  // RC-WEB-3 §4 — sin estimación configurada NO hay conversión que mostrar.
  const isApproxDisplay = display
    ? display.display_is_charge_currency === false && estimateConfigured
    : false;
  const chargeLabel = useCallback((m) => {
    const c = display?.actual_charge?.[String(m)];
    if (!c) return null;
    return `${display?.pen_symbol || "S/"} ${Number(c.amount).toLocaleString("es")}`;
  }, [display]);
  // Etiqueta lista para pintar: "≈ $ 13 aprox." fuera de PEN, "S/ 50" en la moneda de cobro.
  const priceLabelSemantic = useCallback((m) => {
    const label = priceLabel(m);
    if (!label) return null;
    return isApproxDisplay ? `≈ ${label} ${t("pricing.aprox")}` : label;
  }, [priceLabel, isApproxDisplay, t]);
  const chargeNote = useCallback((m) => {
    if (!isApproxDisplay) return null;
    const pen = chargeLabel(m);
    return pen ? t("pricing.charge_real", { pen }) : null;
  }, [isApproxDisplay, chargeLabel, t]);
  const approxHint = useCallback(() => {
    if (!isApproxDisplay) return null;
    return t("pricing.approx_hint", { cur: displayCurrency });
  }, [isApproxDisplay, displayCurrency, t]);
  // RC-WEB-3 §4 — nota explícita cuando el país no tiene estimación local configurada.
  const noEstimateNote = useCallback(() => {
    if (!display || estimateConfigured || isPE) return null;
    return t("pricing.no_local_estimate", { cur: displayCurrency });
  }, [display, estimateConfigured, isPE, displayCurrency, t]);

  // Regla A: cero precios hardcodeados — localiza menciones S/50 y S/80 de textos CMS.
  // Regla 4-D: cero rieles ajenos — un visitante de IN/FR jamás lee "Yape" (frases exactas 4 idiomas + red de seguridad).
  const localizeText = useCallback((text) => {
    if (!text || isPE) return text;
    let s = String(text);
    if (priceLabelSemantic(6)) {
      s = s
        .replaceAll("S/ 50", priceLabelSemantic(6)).replaceAll("S/50", priceLabelSemantic(6))
        .replaceAll("S/ 80", priceLabelSemantic(12)).replaceAll("S/80", priceLabelSemantic(12));
    }
    const isIN = display?.country === "IN";
    const LIST_PHRASES = [
      ["Yape, Plin, transferencia o Mercado Pago (link)", "UPI", "transferencia o Mercado Pago (link)"],
      ["Yape, Plin, bank transfer, or Mercado Pago (link)", "UPI", "bank transfer or Mercado Pago (link)"],
      ["Yape, Plin, transferência ou Mercado Pago (link)", "UPI", "transferência ou Mercado Pago (link)"],
      ["Yape, Plin, virement ou Mercado Pago (lien)", "UPI", "virement ou Mercado Pago (lien)"],
      ["por transferencia o Yape", "por UPI", "por transferencia"],
      ["by bank transfer or Yape", "by UPI", "by bank transfer"],
      ["por transferência ou Yape", "por UPI", "por transferência"],
      ["par virement ou Yape", "par UPI", "par virement"],
    ];
    for (const [phrase, inRepl, intlRepl] of LIST_PHRASES) {
      s = s.replaceAll(phrase, isIN ? inRepl : intlRepl);
    }
    // Red de seguridad: cualquier mención suelta que quede
    const rail = isIN ? "UPI" : "Mercado Pago";
    s = s.replace(/Yape\s*[,y/]?\s*Plin/gi, rail).replace(/\bYape\b/g, rail).replace(/\bPlin\b/g, rail);
    s = s.replace(new RegExp(`${rail}(\\s*[,yoe/]|\\s+or|\\s+ou)?\\s*${rail}`, "g"), rail);
    return s;
  }, [isPE, priceLabelSemantic, display]);

  // RC-WEB-2 — helpers del catálogo canónico de planes
  const planList = useMemo(() => plans || [], [plans]);
  const defaultPlan = planList.find((p) => p.is_default) || planList[0] || null;
  const featuredPlan = planList.find((p) => p.featured) || defaultPlan;
  const planByMonths = useCallback(
    (m) => planList.find((p) => Number(p.duration_months) === Number(m)) || null,
    [planList],
  );
  const planName = useCallback((m) => planByMonths(m)?.name || null, [planByMonths]);
  const planPriceLabel = useCallback((m) => {
    const plan = planByMonths(m);
    if (!plan) return null;
    if (plan.display_estimate) {
      return `≈ ${plan.display_estimate.symbol || plan.display_estimate.currency} ${Number(plan.display_estimate.amount).toLocaleString("es")} ${t("pricing.aprox")}`;
    }
    return `${plan.actual_charge.symbol} ${Number(plan.actual_charge.amount).toLocaleString("es")}`;
  }, [planByMonths, t]);
  const planChargeLabel = useCallback((m) => {
    const plan = planByMonths(m);
    return plan ? `${plan.actual_charge.symbol} ${Number(plan.actual_charge.amount).toLocaleString("es")}` : null;
  }, [planByMonths]);

  const value = {
    country: country || "", setCountry, planMonths, setPlanMonths, display, cur,
    planAmount, planPen, priceLabel, penLabel, localizeText, isPE, ownPrice,
    isApproxDisplay, chargeLabel, priceLabelSemantic, chargeNote, approxHint,
    estimateConfigured, displayCurrency, noEstimateNote,
    plans: planList, configVersion, pricingLoading, defaultPlan, featuredPlan,
    planByMonths, planName, planPriceLabel, planChargeLabel,
  };
  return <PricingCtx.Provider value={value}>{children}</PricingCtx.Provider>;
}

export const usePricing = () => useContext(PricingCtx) || {
  country: "", setCountry: () => {}, planMonths: 12, setPlanMonths: () => {}, display: null,
  cur: "", planAmount: () => null, planPen: () => null,
  priceLabel: () => null, penLabel: () => null, localizeText: (t) => t, isPE: false, ownPrice: false,
  isApproxDisplay: false, chargeLabel: () => null, priceLabelSemantic: () => null,
  chargeNote: () => null, approxHint: () => null,
  estimateConfigured: false, displayCurrency: "", noEstimateNote: () => null,
  plans: [], configVersion: null, pricingLoading: false, defaultPlan: null, featuredPlan: null,
  planByMonths: () => null, planName: () => null, planPriceLabel: () => null, planChargeLabel: () => null,
};
